/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    followUp,
    queueFilter,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        follow_up: followUp,
        legacy_queue: queueFilter,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  legacyQueueSummary({ inboxId } = {}) {
    return axios.get(`${this.url}/legacy_queue_summary`, {
      params: { inbox_id: inboxId || undefined },
    });
  }

  toggleStatus({
    conversationId,
    status,
    snoozedUntil = null,
    followUpReason,
    resolutionReason,
    expectedAssigneeId,
    cancelFollowUp,
  }) {
    const payload = {
      status,
      snoozed_until: snoozedUntil,
    };
    if (resolutionReason !== undefined) {
      payload.resolution_reason = resolutionReason;
    }
    if (followUpReason !== undefined) {
      payload.follow_up_reason = followUpReason;
    }
    if (expectedAssigneeId !== undefined) {
      payload.expected_assignee_id = expectedAssigneeId;
    }
    if (cancelFollowUp !== undefined) {
      payload.cancel_follow_up = cancelFollowUp;
    }

    return axios.post(`${this.url}/${conversationId}/toggle_status`, payload);
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId, assigneeType }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
      assignee_type: assigneeType,
    });
  }

  mergeCandidates(conversationId) {
    return axios.get(`${this.url}/${conversationId}/merge`);
  }

  merge(sourceId, targetId, assigneeId) {
    return axios.post(`${this.url}/${sourceId}/merge`, {
      target_id: targetId,
      assignee_id: assigneeId,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  takeOverFollowUp({ conversationId, expectedOwnerId }) {
    return axios.post(`${this.url}/${conversationId}/take_over_follow_up`, {
      expected_owner_id: expectedOwnerId,
    });
  }

  acceptTransfer(conversationId, transferId) {
    return axios.post(`${this.url}/${conversationId}/accept_transfer`, {
      transfer_id: transferId,
    });
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  teamQuiet(conversationId) {
    return axios.post(`${this.url}/${conversationId}/team_quiet`);
  }

  teamUnquiet(conversationId) {
    return axios.post(`${this.url}/${conversationId}/team_unquiet`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  pin(conversationId) {
    return axios.post(`${this.url}/${conversationId}/pin`);
  }

  getPinned() {
    return axios.get(`${this.url}/pinned`);
  }

  unpin(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unpin`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
